import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Typography,
  Collapse,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material'
import {
  Assessment as AssessmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material'
import { guardReportsApi, institutionService } from '../../services/api'
import { 
  Institution,
  GuardReportStandardResponse,
  GuardReportDetailedResponse
} from '../../types/api'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const formatCurrency = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(numValue || 0)
}

const formatNumber = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(numValue || 0)
}

// Normalizare text pentru PDF (elimină diacritice)
const normalizePDFText = (text: string): string => {
  return text
    .replace(/ă/g, 'a')
    .replace(/Ă/g, 'A')
    .replace(/â/g, 'a')
    .replace(/Â/g, 'A')
    .replace(/î/g, 'i')
    .replace(/Î/g, 'I')
    .replace(/ș/g, 's')
    .replace(/Ș/g, 'S')
    .replace(/ț/g, 't')
    .replace(/Ț/g, 'T')
}

const GuardReportsInstitutionTab: React.FC = () => {
  const today = new Date().toISOString().split('T')[0]
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]

  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [selectedInstitution, setSelectedInstitution] = useState('')
  const [startDate, setStartDate] = useState(firstDayOfMonth)
  const [endDate, setEndDate] = useState(today)
  
  const [useDetailedReport, setUseDetailedReport] = useState(false)
  
  const [standardReport, setStandardReport] = useState<GuardReportStandardResponse | null>(null)
  const [detailedReport, setDetailedReport] = useState<GuardReportDetailedResponse | null>(null)
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedDoctors, setExpandedDoctors] = useState<Set<string>>(new Set())

  // Export functions
  const exportStandardToCSV = () => {
    if (!standardReport) return
    
    const rows = [
      ['Raport Gardă - Instituție Standard'],
      [`Perioada: ${new Date(standardReport.period_start).toLocaleDateString()} - ${new Date(standardReport.period_end).toLocaleDateString()}`],
      [],
      ['Nume Instituție', 'Total Ore Gardă', 'Preț Oră (RON)', 'TOTAL (RON)'],
      [
        standardReport.institution_name,
        formatNumber(standardReport.total_hours),
        formatNumber(standardReport.hourly_rate),
        formatNumber(standardReport.total_amount)
      ]
    ]
    
    const csvContent = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `raport_garda_${standardReport.institution_name}_${startDate}_${endDate}.csv`
    link.click()
  }

  const exportDetailedToCSV = () => {
    if (!detailedReport) return
    
    const rows = [
      ['Raport Gardă - Instituție Detaliat'],
      [`Instituție: ${detailedReport.institution_name}`],
      [`Perioada: ${new Date(detailedReport.period_start).toLocaleDateString()} - ${new Date(detailedReport.period_end).toLocaleDateString()}`],
      [`Total Instituție: ${formatNumber(detailedReport.total_amount)} RON`],
      []
    ]
    
    detailedReport.doctors.forEach(doctor => {
      rows.push([`Medic: ${doctor.doctor_name}`])
      rows.push([`Ore: ${formatNumber(doctor.total_hours)}h × ${formatNumber(doctor.hourly_rate)} RON = ${formatNumber(doctor.total_amount)} RON`])
      rows.push(['Nume Pacient', 'CNP Pacient', 'Descriere Studiu', 'Data Finalizare'])
      
      if (doctor.studies.length === 0) {
        rows.push(['Niciun studiu finalizat'])
      } else {
        doctor.studies.forEach(study => {
          rows.push([
            study.patient_name,
            study.patient_cnp || '-',
            study.study_description || '-',
            new Date(study.finalized_at).toLocaleString()
          ])
        })
      }
      rows.push([])
    })
    
    const csvContent = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `raport_garda_detaliat_${detailedReport.institution_name}_${startDate}_${endDate}.csv`
    link.click()
  }

  const exportStandardToPDF = () => {
    if (!standardReport) return
    
    const doc = new jsPDF('portrait', 'mm', 'a4')
    
    // Title
    doc.setFontSize(16)
    doc.text(normalizePDFText('Raport Garda - Institutie Standard'), 14, 20)
    
    doc.setFontSize(10)
    doc.text(normalizePDFText(`Perioada: ${new Date(standardReport.period_start).toLocaleDateString()} - ${new Date(standardReport.period_end).toLocaleDateString()}`), 14, 28)
    
    // Table
    autoTable(doc, {
      startY: 35,
      head: [[normalizePDFText('Nume Institutie'), normalizePDFText('Total Ore Garda'), normalizePDFText('Pret Ora (RON)'), 'TOTAL (RON)']],
      body: [[
        normalizePDFText(standardReport.institution_name),
        formatNumber(standardReport.total_hours) + 'h',
        formatNumber(standardReport.hourly_rate),
        formatNumber(standardReport.total_amount)
      ]],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10, cellPadding: 3 }
    })
    
    doc.save(normalizePDFText(`raport_garda_${standardReport.institution_name}_${startDate}_${endDate}.pdf`))
  }

  const exportDetailedToPDF = () => {
    if (!detailedReport) return
    
    const doc = new jsPDF('landscape', 'mm', 'a4')
    
    // Title
    doc.setFontSize(16)
    doc.text(normalizePDFText('Raport Garda - Institutie Detaliat'), 14, 20)
    
    doc.setFontSize(10)
    doc.text(normalizePDFText(`Institutie: ${detailedReport.institution_name}`), 14, 28)
    doc.text(normalizePDFText(`Perioada: ${new Date(detailedReport.period_start).toLocaleDateString()} - ${new Date(detailedReport.period_end).toLocaleDateString()}`), 14, 34)
    doc.text(normalizePDFText(`Total Institutie: ${formatCurrency(detailedReport.total_amount)}`), 14, 40)
    
    let currentY = 48
    
    detailedReport.doctors.forEach((doctor) => {
      // Check if we need a new page
      if (currentY > 180) {
        doc.addPage()
        currentY = 20
      }
      
      // Doctor header
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(normalizePDFText(doctor.doctor_name), 14, currentY)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text(normalizePDFText(`${formatNumber(doctor.total_hours)}h × ${formatCurrency(doctor.hourly_rate)} = ${formatCurrency(doctor.total_amount)}`), 14, currentY + 5)
      
      currentY += 10
      
      // Studies table
      const studiesData = doctor.studies.length === 0 
        ? [[normalizePDFText('Niciun studiu finalizat'), '', '', '']]
        : doctor.studies.map(study => [
            normalizePDFText(study.patient_name),
            study.patient_cnp || '-',
            normalizePDFText(study.study_description || '-'),
            new Date(study.finalized_at).toLocaleString()
          ])
      
      autoTable(doc, {
        startY: currentY,
        head: [[normalizePDFText('Nume Pacient'), 'CNP Pacient', normalizePDFText('Descriere Studiu'), 'Data Finalizare']],
        body: studiesData,
        theme: 'grid',
        headStyles: { fillColor: [52, 152, 219], textColor: 255, fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 80 },
          3: { cellWidth: 50 }
        }
      })
      
      currentY = (doc as any).lastAutoTable.finalY + 8
    })
    
    doc.save(normalizePDFText(`raport_garda_detaliat_${detailedReport.institution_name}_${startDate}_${endDate}.pdf`))
  }

  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const data = await institutionService.getInstitutions({ limit: 200 })
        setInstitutions(data.institutions)
      } catch (err) {
        console.error('Error loading institutions', err)
        setError('Eroare la încărcarea instituțiilor')
      }
    }
    loadInstitutions()
  }, [])

  // Când se schimbă instituția, resetăm tipul de raport
  useEffect(() => {
    if (selectedInstitution) {
      setUseDetailedReport(false)
    }
  }, [selectedInstitution])

  const handleGenerateReport = async () => {
    if (!selectedInstitution || !startDate || !endDate) {
      setError('Vă rugăm completați toate câmpurile')
      return
    }

    setError(null)
    setLoading(true)
    setStandardReport(null)
    setDetailedReport(null)

    try {
      const request = {
        institution_id: selectedInstitution,
        start_date: startDate,
        end_date: endDate
      }

      if (useDetailedReport) {
        // Raport detaliat (cu PACS)
        const report = await guardReportsApi.getInstitutionDetailedReport(request)
        setDetailedReport(report)
      } else {
        // Raport standard
        const report = await guardReportsApi.getInstitutionStandardReport(request)
        setStandardReport(report)
      }
    } catch (err: any) {
      console.error('Error generating report', err)
      setError(err.response?.data?.detail || 'Eroare la generarea raportului')
    } finally {
      setLoading(false)
    }
  }

  const toggleDoctorExpand = (doctorId: string) => {
    setExpandedDoctors(prev => {
      const newSet = new Set(prev)
      if (newSet.has(doctorId)) {
        newSet.delete(doctorId)
      } else {
        newSet.add(doctorId)
      }
      return newSet
    })
  }

  // Styling compact pentru input-uri
  const inputSx = { 
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, 
    '& .MuiInputLabel-root': { fontSize: '0.7rem' }
  }

  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <AssessmentIcon color="primary" sx={{ fontSize: '1rem' }} />
          Rapoarte Gardă - Instituții
        </Typography>
        
        {/* Filters inline */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, ...inputSx }}>
            <InputLabel>Instituție</InputLabel>
            <Select
              label="Instituție"
              value={selectedInstitution}
              onChange={(e) => setSelectedInstitution(e.target.value)}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Selectează</MenuItem>
              {institutions.map((inst) => (
                <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.75rem' }}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="De la"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130, ...inputSx }}
          />
          
          <TextField
            label="Până la"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130, ...inputSx }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={useDetailedReport}
                onChange={(e) => setUseDetailedReport(e.target.checked)}
                disabled={loading}
                sx={{ p: 0.25 }}
              />
            }
            label={<Typography sx={{ fontSize: '0.72rem' }}>Detaliat</Typography>}
            sx={{ ml: 0, mr: 1 }}
          />
          
          <Button
            variant="outlined"
            size="small"
            onClick={handleGenerateReport}
            disabled={loading || !selectedInstitution}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 2 }}
          >
            {loading ? <CircularProgress size={14} /> : 'Generează'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Raport Standard - compact */}
      {standardReport && (
        <Paper sx={{ p: 1.5, mb: 2, '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 }, '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {standardReport.institution_name} - Raport Standard
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
            Perioada: {new Date(standardReport.period_start).toLocaleDateString()} - {new Date(standardReport.period_end).toLocaleDateString()}
          </Typography>
          
          <TableContainer sx={{ mt: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  bgcolor: 'grey.900',
                  '& th': {
                    color: 'common.white',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    py: 1,
                    px: 1.5,
                    whiteSpace: 'nowrap'
                  }
                }}>
                  <TableCell>Instituție</TableCell>
                  <TableCell align="right">Ore Gardă</TableCell>
                  <TableCell align="right">Preț Oră</TableCell>
                  <TableCell align="right">TOTAL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{standardReport.institution_name}</TableCell>
                  <TableCell align="right">{formatNumber(standardReport.total_hours)}h</TableCell>
                  <TableCell align="right">{formatCurrency(standardReport.hourly_rate)}</TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(standardReport.total_amount)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: '0.9rem' }} />} onClick={exportStandardToPDF} sx={{ fontSize: '0.78rem' }}>
              PDF
            </Button>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: '0.9rem' }} />} onClick={exportStandardToCSV} sx={{ fontSize: '0.78rem' }}>
              CSV
            </Button>
          </Box>
        </Paper>
      )}

      {/* Raport Detaliat - compact */}
      {detailedReport && (
        <Paper sx={{ p: 1.5, '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 }, '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {detailedReport.institution_name} - Raport Detaliat
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
            Perioada: {new Date(detailedReport.period_start).toLocaleDateString()} - {new Date(detailedReport.period_end).toLocaleDateString()}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 1.5, mb: 2 }} color="primary">
            Total: {formatCurrency(detailedReport.total_amount)}
          </Typography>

          {detailedReport.doctors.map((doctor) => (
            <Box key={doctor.doctor_id} sx={{ mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.selected'
                  }
                }}
                onClick={() => toggleDoctorExpand(doctor.doctor_id)}
              >
                <Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {doctor.doctor_name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
                    {formatNumber(doctor.total_hours)}h × {formatCurrency(doctor.hourly_rate)} = {formatCurrency(doctor.total_amount)}
                  </Typography>
                </Box>
                <IconButton size="small">
                  {expandedDoctors.has(doctor.doctor_id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={expandedDoctors.has(doctor.doctor_id)}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        bgcolor: 'grey.900',
                        '& th': {
                          color: 'common.white',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          py: 0.75,
                          px: 1,
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell>Pacient</TableCell>
                        <TableCell>CNP</TableCell>
                        <TableCell>Studiu</TableCell>
                        <TableCell>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {doctor.studies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Niciun studiu finalizat în această perioadă
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        doctor.studies.map((study, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{study.patient_name}</TableCell>
                            <TableCell>{study.patient_cnp || '—'}</TableCell>
                            <TableCell>{study.study_description || '—'}</TableCell>
                            <TableCell>{new Date(study.finalized_at).toLocaleString()}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          ))}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportDetailedToPDF}>
              Export PDF
            </Button>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportDetailedToCSV}>
              Export CSV
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default GuardReportsInstitutionTab

